// src/tattoo/BrowseArtistsPage.jsx
//
// Public /artists browse index. Anyone can hit this URL — no auth — so it
// doubles as Tattune's discovery surface AND a funnel into the editor for
// visitors who came in via a single artist's share link.
//
// Sibling to ArtistPublicPage.jsx (the per-artist profile at
// /artists/<slug>). Shares the same dark theme + the helpers in
// lib/shopUtils. Distinct from components/ArtistDirectoryBrowser.jsx,
// which is the in-editor left-panel browser — different context (narrow
// column, in-app), different card UX (expand-in-place), so kept separate
// rather than over-genericized.
//
// Filter state lives in the URL (`?q=`, `?style=`, `?sort=`) so a
// filtered view is shareable and survives a refresh.

import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "./lib/supabaseClient";
import {
  formatLocation,
  getStyleArray,
  getInitials,
} from "./lib/shopUtils";

// ---------- shared resize-aware narrow-viewport hook ----------
// Same pattern as ArtistPublicPage's useIsNarrow. Kept here too so this
// file has no dependency on a sibling that might move. If we ever build a
// shared `hooks/useIsNarrow.js` module, both files can collapse onto it.
function useIsNarrow(maxWidth = 720) {
  const query = `(max-width: ${maxWidth - 1}px)`;
  const [match, setMatch] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatch(e.matches);
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);
  return match;
}

// ---------- distance helpers (haversine, km) ----------

function getShopCoords(shop) {
  const lat = shop.latitude ?? shop.lat ?? shop.lat_deg ?? null;
  const lng = shop.longitude ?? shop.lng ?? shop.lon ?? shop.long_deg ?? null;
  if (typeof lat === "number" && typeof lng === "number") {
    return { lat, lng };
  }
  return null;
}

function distanceKm(userLoc, shop) {
  if (!userLoc) return null;
  const c = getShopCoords(shop);
  if (!c) return null;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(c.lat - userLoc.lat);
  const dLon = toRad(c.lng - userLoc.lng);
  const lat1 = toRad(userLoc.lat);
  const lat2 = toRad(c.lat);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDistance(km) {
  if (km == null || !isFinite(km)) return "";
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${Math.round(km)} km`;
}

// ---------- main page ----------

export default function BrowseArtistsPage() {
  const isMobile = useIsNarrow(720);
  const [searchParams, setSearchParams] = useSearchParams();

  const query = searchParams.get("q") || "";
  const styleFilter = searchParams.get("style") || null;
  const sortMode = searchParams.get("sort") || "default";

  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [userLoc, setUserLoc] = useState(null);
  // "idle" | "requesting" | "granted" | "denied" | "unavailable"
  const [geoState, setGeoState] = useState("idle");

  // Set the browser tab title for in-app UX. Crawlers see the defaults
  // baked into index.html (TT_META_START..TT_META_END block).
  useEffect(() => {
    const previous = document.title;
    document.title = "Browse tattoo artists | Tattune";
    return () => {
      document.title = previous;
    };
  }, []);

  // (Previously: a useEffect here overrode the global overflow:hidden
  // from tattoo.css. That global rule has been removed and the editor
  // scopes its own viewport lock, so this page just scrolls naturally.)

  // Load all active shops once. `is_active=true` is the canonical
  // "listed publicly" flag — RLS-enforced, lifecycle-managed (auto-
  // flipped to false when entitlement lapses), and shared with the
  // Edge Functions and in-editor surfaces. The older `is_visible`
  // column is retired but still present in the schema; no read should
  // reference it any more.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const { data, error } = await supabase
          .from("shops")
          .select("*")
          .eq("is_active", true)
          .order("display_name", { ascending: true });
        if (error) throw error;
        if (cancelled) return;
        setShops(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("[browse] failed to load shops", err);
        if (!cancelled) setError("Couldn't load artists right now.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Top 8 styles by raw frequency across visible shops, used as the
  // quick-filter pill row.
  const allStyles = useMemo(() => {
    const counts = new Map();
    for (const shop of shops) {
      for (const s of getStyleArray(shop)) {
        counts.set(s, (counts.get(s) || 0) + 1);
      }
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([style]) => style);
  }, [shops]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    const hasTerm = term.length > 0;
    const hasStyle = !!styleFilter;

    let result = shops.filter((shop) => {
      if (hasStyle) {
        const styles = getStyleArray(shop).map((s) => s.toLowerCase());
        if (!styles.includes(styleFilter.toLowerCase())) return false;
      }
      if (!hasTerm) return true;
      const loc = formatLocation(shop);
      const styles = getStyleArray(shop).join(" ");
      const haystack = [
        shop.display_name,
        shop.name,
        shop.tagline,
        shop.bio,
        loc,
        styles,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });

    if (sortMode === "nearest" && userLoc) {
      result = result.slice().sort((a, b) => {
        const da = distanceKm(userLoc, a) ?? Number.POSITIVE_INFINITY;
        const db = distanceKm(userLoc, b) ?? Number.POSITIVE_INFINITY;
        return da - db;
      });
    } else {
      result = result.slice().sort((a, b) => {
        const na = (a.display_name || a.name || "").toLowerCase();
        const nb = (b.display_name || b.name || "").toLowerCase();
        return na.localeCompare(nb);
      });
    }

    return result;
  }, [shops, query, styleFilter, sortMode, userLoc]);

  // ---------- filter mutation helpers ----------

  function patchParams(patch) {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v == null || v === "") next.delete(k);
      else next.set(k, v);
    }
    setSearchParams(next, { replace: true });
  }

  function clearFilters() {
    setSearchParams({}, { replace: true });
  }

  function requestGeoAndSortNearest() {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGeoState("unavailable");
      return;
    }
    setGeoState("requesting");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLoc({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        setGeoState("granted");
        patchParams({ sort: "nearest" });
      },
      () => setGeoState("denied"),
      { enableHighAccuracy: false, timeout: 5000 }
    );
  }

  const hasAnyFilter = !!query || !!styleFilter || sortMode !== "default";

  return (
    <div
      className="tattoo-app theme-light"
      style={{
        minHeight: "100vh",
        background: "var(--bg-app)",
        color: "var(--fg)",
      }}
    >
      <div
        style={{
          maxWidth: 1180,
          margin: "0 auto",
          padding: "28px 16px 72px",
          display: "grid",
          gap: 28,
        }}
      >
        {/* Header — minimal nav so a /artists visitor still has a way to
            sign in / open the editor. */}
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <Link
            to="/"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              textDecoration: "none",
              color: "var(--fg)",
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: "0.02em",
            }}
          >
            <img
              src="/tattune-logo-blk.svg"
              alt="Tattune"
              style={{ height: 40, width: "auto", display: "block" }}
            />
          </Link>
          <Link
            to="/"
            style={{
              fontSize: 13,
              color: "var(--fg-78)",
              textDecoration: "none",
              border: "1px solid var(--line-18)",
              padding: "8px 14px",
              borderRadius: 999,
            }}
          >
            Sign in
          </Link>
        </header>

        {/* Title + subtitle */}
        <div style={{ display: "grid", gap: 8 }}>
          <h1
            style={{
              margin: 0,
              fontSize: isMobile ? 26 : 36,
              lineHeight: 1.1,
              letterSpacing: "0.01em",
            }}
          >
            Browse tattoo artists
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: 15,
              color: "var(--fg-70)",
              maxWidth: 680,
              lineHeight: 1.6,
            }}
          >
            Find a studio, view their flash, and try designs on the
            Plannequin before booking.
          </p>
        </div>

        {/* Filter row */}
        <div style={{ display: "grid", gap: 12 }}>
          <input
            type="search"
            value={query}
            onChange={(e) => patchParams({ q: e.target.value })}
            placeholder="Search by studio, city, style, or bio…"
            aria-label="Search artists"
            style={{
              width: "100%",
              padding: "12px 14px",
              fontSize: 15,
              borderRadius: 14,
              border: "1px solid var(--line-14)",
              background: "var(--tint-04)",
              color: "var(--fg)",
              outline: "none",
              boxSizing: "border-box",
            }}
          />

          {/* Style pills */}
          {allStyles.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                alignItems: "center",
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  color: "var(--fg-50)",
                  width: 44,
                  flex: "0 0 44px",
                }}
              >
                Style:
              </span>
              {allStyles.map((style) => {
                const on =
                  !!styleFilter &&
                  styleFilter.toLowerCase() === style.toLowerCase();
                return (
                  <button
                    key={style}
                    type="button"
                    aria-pressed={on}
                    onClick={() => patchParams({ style: on ? null : style })}
                    style={{
                      cursor: "pointer",
                      borderRadius: 999,
                      padding: "5px 12px",
                      fontSize: 12,
                      textTransform: "capitalize",
                      border: on
                        ? "1px solid var(--accent)"
                        : "1px solid var(--line-18)",
                      background: on
                        ? "rgba(162,51,43,0.85)"
                        : "var(--tint-04)",
                      color: on ? "#fff" : "var(--fg-90)",
                      transition:
                        "background 0.15s ease, border-color 0.15s ease",
                    }}
                  >
                    {style}
                  </button>
                );
              })}
            </div>
          )}

          {/* Sort + clear-filters row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 6,
              fontSize: 12,
              color: "var(--fg-50)",
            }}
          >
            <span style={{ width: 44, flex: "0 0 44px" }}>Sort:</span>
            <button
              type="button"
              aria-pressed={sortMode === "default"}
              onClick={() => patchParams({ sort: null })}
              style={pillBtn(sortMode === "default")}
            >
              Name
            </button>
            <button
              type="button"
              aria-pressed={sortMode === "nearest"}
              onClick={() => {
                if (sortMode === "nearest") {
                  patchParams({ sort: null });
                } else if (userLoc) {
                  patchParams({ sort: "nearest" });
                } else {
                  requestGeoAndSortNearest();
                }
              }}
              disabled={geoState === "requesting"}
              style={pillBtn(sortMode === "nearest")}
              title={
                geoState === "denied"
                  ? "Location permission denied — re-enable in your browser to sort by nearest"
                  : geoState === "unavailable"
                  ? "Geolocation isn't available on this device"
                  : undefined
              }
            >
              {geoState === "requesting" ? "Locating…" : "Nearest"}
            </button>

            {hasAnyFilter && (
              <button
                type="button"
                onClick={clearFilters}
                style={{
                  marginLeft: "auto",
                  cursor: "pointer",
                  border: "none",
                  background: "transparent",
                  color: "var(--fg-70)",
                  fontSize: 12,
                  padding: "4px 8px",
                  textDecoration: "underline",
                }}
              >
                Clear filters
              </button>
            )}
          </div>

          {(geoState === "denied" || geoState === "unavailable") &&
            sortMode !== "nearest" && (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--danger)",
                  marginTop: -4,
                }}
              >
                {geoState === "denied"
                  ? "Location permission was denied. Sort-by-nearest needs your location to work."
                  : "Geolocation isn't available on this device."}
              </div>
            )}
        </div>

        {/* Result count */}
        {!loading && !error && (
          <div
            style={{
              fontSize: 13,
              color: "var(--fg-50)",
            }}
          >
            {filtered.length === 0
              ? "No artists match these filters."
              : `${filtered.length} ${filtered.length === 1 ? "artist" : "artists"}`}
          </div>
        )}

        {/* States */}
        {loading && (
          <div
            style={{
              padding: "40px 0",
              textAlign: "center",
              color: "var(--fg-60)",
            }}
          >
            Loading artists…
          </div>
        )}

        {error && !loading && (
          <div
            role="alert"
            style={{
              borderRadius: 14,
              padding: 16,
              background: "color-mix(in srgb, var(--danger) 12%, var(--bg-app))",
              border: "1px solid color-mix(in srgb, var(--danger) 40%, transparent)",
              color: "var(--danger)",
            }}
          >
            {error}
          </div>
        )}

        {/* Grid */}
        {!loading && !error && filtered.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: 16,
            }}
          >
            {filtered.map((shop) => (
              <ArtistCard
                key={shop.id}
                shop={shop}
                userLoc={sortMode === "nearest" ? userLoc : null}
              />
            ))}
          </div>
        )}

        {/* Empty state with reset */}
        {!loading && !error && filtered.length === 0 && hasAnyFilter && (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              alignSelf: "start",
              padding: "10px 16px",
              fontSize: 14,
              borderRadius: 14,
              border: "1px solid var(--line-18)",
              background: "var(--tint-04)",
              color: "var(--fg)",
              cursor: "pointer",
            }}
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}

function pillBtn(active) {
  return {
    cursor: "pointer",
    borderRadius: 999,
    padding: "4px 12px",
    fontSize: 12,
    border: active
      ? "1px solid var(--fg)"
      : "1px solid var(--line-18)",
    background: active ? "var(--tint-08)" : "transparent",
    color: active ? "var(--fg)" : "var(--fg-78)",
  };
}

function ArtistCard({ shop, userLoc }) {
  const displayName = shop.display_name || shop.name || "Studio";
  const loc = formatLocation(shop);
  const styles = getStyleArray(shop).slice(0, 3);
  const heroImage =
    shop.hero_image_url ||
    shop.cover_image_url ||
    shop.banner_image_url ||
    null;
  const avatarSrc = shop.avatar_url || shop.logo_url || null;
  const km = userLoc ? distanceKm(userLoc, shop) : null;
  const distLabel = km != null ? formatDistance(km) : "";

  return (
    <Link
      to={`/artists/${shop.slug}`}
      style={{
        display: "flex",
        flexDirection: "column",
        textDecoration: "none",
        color: "inherit",
        background: "var(--tint-04)",
        border: "1px solid var(--line-08)",
        borderRadius: 18,
        overflow: "hidden",
        transition: "border-color 0.15s ease, transform 0.15s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "var(--line-18)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--line-08)";
      }}
    >
      {/* Hero / placeholder */}
      <div
        style={{
          width: "100%",
          aspectRatio: "4 / 3",
          background: "var(--bg-panel-2)",
          position: "relative",
        }}
      >
        {heroImage ? (
          <img
            src={heroImage}
            alt={`${displayName} featured work`}
            loading="lazy"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              color: "var(--fg-35)",
              fontSize: 64,
              fontWeight: 800,
              letterSpacing: "0.04em",
              lineHeight: 1,
            }}
          >
            {avatarSrc ? null : getInitials(displayName)}
          </div>
        )}
      </div>

      {/* Body */}
      <div
        style={{
          padding: 14,
          display: "grid",
          gap: 6,
          flex: 1,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          {avatarSrc && (
            <img
              src={avatarSrc}
              alt=""
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              style={{
                width: 32,
                height: 32,
                borderRadius: 999,
                objectFit: "cover",
                background: "var(--bg-panel-2)",
                flex: "0 0 auto",
              }}
            />
          )}
          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              lineHeight: 1.2,
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={displayName}
          >
            {displayName}
          </div>
        </div>

        {loc && (
          <div
            style={{
              fontSize: 13,
              color: "var(--fg-60)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={loc}
          >
            {loc}
          </div>
        )}

        {styles.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {styles.map((s) => (
              <span
                key={s}
                style={{
                  fontSize: 11,
                  textTransform: "capitalize",
                  padding: "2px 8px",
                  borderRadius: 999,
                  border: "1px solid var(--line-08)",
                  color: "var(--fg-78)",
                }}
              >
                {s}
              </span>
            ))}
          </div>
        )}

        {distLabel && (
          <div
            style={{
              marginTop: 2,
              fontSize: 12,
              color: "var(--fg-50)",
            }}
          >
            {distLabel} away
          </div>
        )}
      </div>
    </Link>
  );
}
